/**
 * BookEditorView — Phase 3
 * Container: chapter list sidebar + editor panel.
 * Route: /books/:bookId/editor
 *
 * While book.status === 'writing', polls chapters every 8s so newly completed
 * chapters appear progressively. The sidebar shows all outline chapters from the
 * start — complete, generating, and pending — so users can see the full book
 * structure and read chapters as they finish.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { ApiSuccess } from '../../lib/api';
import type { ChapterDocument, BookDocument } from '../../types/book';
import { useAuthStore } from '../../stores/authStore';
import { ChapterNavSidebar } from './ChapterNavSidebar';
import type { ChapterEntry } from './ChapterNavSidebar';
import { ChapterEditorPanel } from './ChapterEditorPanel';
import { cn } from '../../lib/utils';

interface BookEditorViewProps {
  book: BookDocument;
  isPaidUser: boolean;
}

function useChapters(bookId: string, bookStatus: string) {
  return useQuery({
    queryKey: ['chapters', bookId],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ chapters: ChapterDocument[] }>>(
        `/books/${bookId}/chapters`,
      );
      return (res.data.data.chapters ?? []).sort(
        (a, b) => a.chapterNumber - b.chapterNumber,
      );
    },
    refetchInterval: bookStatus === 'writing' ? 8000 : false,
  });
}

export function BookEditorView({ book, isPaidUser }: BookEditorViewProps) {
  const { data: chapters = [], isLoading, error } = useChapters(book.id, book.status);
  const user = useAuthStore((s) => s.user);

  const completedChapters = chapters.filter((c) => c.status === 'complete');
  const [activeChapterNum, setActiveChapterNum] = useState<number>(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-select first complete chapter on initial load (in case ch1 isn't ready yet)
  useEffect(() => {
    if (completedChapters.length > 0 && !completedChapters.find((c) => c.chapterNumber === activeChapterNum)) {
      setActiveChapterNum(completedChapters[0].chapterNumber);
    }
  }, [completedChapters.length]);

  // Build merged chapter list: outline chapters as the spine, filled in with actual docs.
  // This shows all chapters from the start — pending ones appear dimmed until they're written.
  const chapterDocMap = new Map(chapters.map((ch) => [ch.chapterNumber, ch]));
  const outlineChapters = book.outline?.chapters ?? [];

  const allChapterEntries: ChapterEntry[] = outlineChapters.map((oc) => {
    const doc = chapterDocMap.get(oc.number);
    if (doc) {
      return {
        chapterNumber: doc.chapterNumber,
        title: doc.title,
        status: doc.status,
        wordCount: doc.wordCount,
        isEdited: doc.isEdited,
        editedWordCount: doc.editedWordCount,
      };
    }
    return {
      chapterNumber: oc.number,
      title: oc.title,
      status: 'pending' as const,
    };
  });

  const activeChapter =
    completedChapters.find((c) => c.chapterNumber === activeChapterNum) ??
    completedChapters[0];

  const bookWordCount = completedChapters.reduce((sum, ch) => {
    return sum + (ch.isEdited && ch.editedWordCount != null ? ch.editedWordCount : ch.wordCount);
  }, 0) || book.wordCount;

  const accessibleChapterCount = isPaidUser
    ? completedChapters.length
    : Math.min(completedChapters.length, 1);

  const accessibleCompleted = isPaidUser
    ? completedChapters
    : completedChapters.filter((c) => c.chapterNumber <= 1);

  const activeIdx = accessibleCompleted.findIndex((c) => c.chapterNumber === activeChapterNum);
  const hasPrevChapter = activeIdx > 0;
  const hasNextChapter = activeIdx < accessibleCompleted.length - 1;

  const goToPrevChapter = () => {
    if (hasPrevChapter) setActiveChapterNum(accessibleCompleted[activeIdx - 1].chapterNumber);
  };
  const goToNextChapter = () => {
    if (hasNextChapter) setActiveChapterNum(accessibleCompleted[activeIdx + 1].chapterNumber);
  };

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center text-neutral-400">
        Loading…
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
          <p className="text-sm text-neutral-400">Loading chapters…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-red-400">
        Failed to load chapters. Please try again.
      </div>
    );
  }

  if (completedChapters.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
        <h2 className="text-lg font-semibold text-white">Your first chapters are being written…</h2>
        <p className="text-sm text-neutral-400 max-w-sm">
          Come back in a moment — your book will appear here as chapters complete.
        </p>
        <Link
          to={`/books/${book.id}`}
          className="text-sm font-medium text-violet-400 hover:text-violet-300 underline"
        >
          ← Back to book
        </Link>
      </div>
    );
  }

  return (
    <div className="relative flex h-full overflow-hidden">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="absolute inset-0 z-20 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Desktop sidebar — always in flex flow */}
      <div className="hidden h-full w-56 shrink-0 md:flex">
        <ChapterNavSidebar
          chapters={allChapterEntries}
          activeChapterNum={activeChapterNum}
          isPaidUser={isPaidUser}
          onSelectChapter={(num) => setActiveChapterNum(num)}
        />
      </div>

      {/* Mobile sidebar — slides in from left as drawer */}
      <div
        className={cn(
          'absolute inset-y-0 left-0 z-30 w-72 transition-transform duration-300 ease-in-out md:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <ChapterNavSidebar
          chapters={allChapterEntries}
          activeChapterNum={activeChapterNum}
          isPaidUser={isPaidUser}
          onSelectChapter={(num) => { setActiveChapterNum(num); setSidebarOpen(false); }}
        />
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden bg-[#0f0f14] min-w-0">
        {/* Mobile-only toggle bar */}
        <div className="flex items-center gap-3 border-b border-neutral-800 bg-neutral-900 px-4 py-2.5 md:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-neutral-200 hover:text-white transition-colors"
          >
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Chapters
          </button>
          {activeChapter && (
            <span className="truncate text-xs text-neutral-500">
              Ch {activeChapter.chapterNumber}: {activeChapter.title}
            </span>
          )}
        </div>

        {activeChapter ? (
          <ChapterEditorPanel
            key={`${book.id}-ch${activeChapter.chapterNumber}`}
            chapter={activeChapter}
            bookId={book.id}
            isPaidUser={isPaidUser}
            bookWordCount={bookWordCount}
            totalChapters={accessibleChapterCount}
            onPrevChapter={goToPrevChapter}
            onNextChapter={goToNextChapter}
            hasPrevChapter={hasPrevChapter}
            hasNextChapter={hasNextChapter}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-neutral-400">
            Select a chapter to begin reading.
          </div>
        )}
      </div>
    </div>
  );
}
